import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactFlow, {
  Background,
  addEdge,
  useReactFlow,
  ReactFlowProvider,
  useStore,
  ReactFlowInstance,
} from "reactflow";
import "reactflow/dist/style.css";
import "./styles.css";
import { v4 as uuidv4 } from "uuid";

import QuestionNode from "../../components/Nodes/QuestionNode";
import AddButtonNode from "../../components/Nodes/AddButtonNode";
import DescriptionNode from "../../components/Nodes/DescriptionNode";
import ChoiceNode from "../../components/Nodes/ChoiceNode";
import QAOptionNode from "../../components/Nodes/QAOptionNode";
import StepsNode from "../../components/Nodes/StepsNode";
import UploadButtonNode from "../../components/Nodes/UploadButtonNode";
import UploadNode from "../../components/Nodes/UploadNode";
import {
  getNodesAndEdgesBasedONDrag,
  transformNodesToQA,
  generateQANodesFromCSV,
  hasInvalidData,
} from "../../utilities/Nodes/getConnectedChildNodes";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LinkNode from "../../components/Nodes/LinkNode";
import { RootState } from "../../service/store";
import {
  setDraftModel,
  setGenerateModel,
  setWorkBenchName,
  setModelTypeFromWorkbench,
  setEmbedingName,
  setDashboardModalsOpens,
  setWorkbenchPermission
} from "../../service/apiSlices/staticApi";
import {
  useLazyGetWorkBenchDataQuery,
  useGenerateModelMutation,
  useDraftModelMutation,
  useGetNodesQuery,
  useGetEdgesQuery,
} from "../../service/apiSlices/workbench";
import { Modal } from "antd";
import { validateName } from "../../utilities/utils";
import { checkParentNodeConnections } from "../../utilities/Nodes/commonFunctions";
import GlobeMinimap from "../../components/GlobeMinimap";
import { showErrorToast, showSuccessToast } from "../../utilities/toastUtils";
import { useTemplateTypeContext } from "../../context/workbench/TemplateTypeContext";
import { useColorContext } from "../../context/workbench/ColorContext";
import { useFlowDataContext } from "../../context/workbench/FlowDataContext";
import { useNodeDataContext } from "../../context/workbench/DataNodeContext";
import { useViewPortContext } from "../../context/workbench/ViewPortContext";
import { useLayerContext } from "../../context/workbench/LayerContext";
import { useZoomContext } from "../../context/workbench/ZoomContext";
import { useNodesPositionContext } from "../../context/workbench/NodesPositionContext";
import { useNodesWithPositionContext } from "../../context/workbench/NodesWithPositionContext";
import { useModelDetailsContext } from "../../context/workbench/ModelDetailsContex";
import { ROUTES } from "../../utilities/Constants/Constants";

const nodeTypes = {
  questionNode: QuestionNode,
  addButtonNode: AddButtonNode,
  descriptionNode: DescriptionNode,
  choiceNode: ChoiceNode,
  qaNode: QAOptionNode,
  stepsNode: StepsNode,
  uploadButtonNode: UploadButtonNode,
  uploadNode: UploadNode,
  linkNode: LinkNode,
};

const Flow = memo(function Flow() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { screenToFlowPosition, getNode, setCenter } = useReactFlow();
  const { type: templateType } = useTemplateTypeContext();
  const colorContext = useColorContext();
  const { nodes, edges, setNodes, setEdges, onNodesChange, onEdgesChange, getNodeMethods } = useFlowDataContext();
  const { nodesPositionMap, onNodeDrag, updateNodesPosition } = useNodesPositionContext();
  const { nodesWithPosition } = useNodesWithPositionContext();
  const dataNodeContext = useNodeDataContext()
  const viewPortContext = useViewPortContext();
  const zoomContext = useZoomContext();
  const LayerContext = useLayerContext();
  const { modelGenerateContextData, setIsGenerateModelContextOpen, setIsPendingStatusModalOpen } = useModelDetailsContext();

  const zoomLevel: any = zoomContext?.workbenchZoomLevel;
  const color: any = colorContext?.color;
  const flowContainerClass = `flow-container-${color}`;

  const generateModel = useSelector((state: RootState) => state?.static?.generateModel);
  const draftModel = useSelector((state: RootState) => state?.static?.draftModel);
  const workBenchName = useSelector((state: RootState) => state?.static?.workBenchName);
  // Get transform values from React Flow store
  const transform = useStore((state) => state.transform);
  const [x, y, zoom] = transform || [0, 0, 1];

  const secret_key = sessionStorage.getItem('secret_key') || "";

  const [viewPortStatus, setViewPortStatus] = useState<boolean>(true);
  const [formatType, setFormatType] = useState<number>();
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
  const reactFlowWrapper = useRef(null);

  const [getModelDetails] = useLazyGetWorkBenchDataQuery();
  const [generateWorkbenchModel] = useGenerateModelMutation();
  const [draftWorkbenchModel] = useDraftModelMutation();
  const { data: getEdgesData, refetch: refetchEdges } = useGetEdgesQuery(secret_key ?? "", { skip: !viewPortStatus });
  const { data: getNodesData, refetch: refetchNodes } = useGetNodesQuery(secret_key ?? "", { skip: !viewPortStatus });

  const validWorkBenchName = useMemo(() => validateName(workBenchName) ? workBenchName : "", [workBenchName]);

  useEffect(() => {
    if (LayerContext?.layerSelectedNodeId) {
      centerNodeInViewport(LayerContext?.layerSelectedNodeId);
    }
  }, [LayerContext?.layerSelectedNodeId]);

  useEffect(() => {
    if (secret_key) {
      getModelDetails(secret_key)
        .unwrap()
        .then((response: any) => {
          if (response?.status_code === 200) {
            showSuccessToast(response?.message);
            if (response?.data?.viewport_data) {
              setViewPortStatus(response?.data?.viewport_data ? true : false);
              refetchEdges();
              refetchNodes();
            } else if (response?.data?.upload_data) {
              const { nodes: generatedNodes, edges: generatedEdges, nodesPosition } =
                generateQANodesFromCSV(response?.data?.upload_data);

              const nextNodes = generatedNodes.map((node: any) => {
                return {
                  ...node,
                  data: { ...node.data, ...getNodeMethods({ methodType: node.type, ndsPositionMap: nodesPosition }) }
                }
              })
              setNodes(nextNodes);
              setEdges(generatedEdges);
              updateNodesPosition(nodesPosition);
            } else {
              setNodes([]);
              setEdges([]);
              updateNodesPosition({}, 'replace')
            }
            dispatch(setWorkBenchName(response?.data?.workspace_name));
            dispatch(setEmbedingName(response?.data?.embedding_name))
            dispatch(setModelTypeFromWorkbench(response?.data?.file_format_type));
            setFormatType(response?.data?.format_type);
            dispatch(setWorkbenchPermission(
              response?.data?.workbench_permissions && response?.data?.workbench_permissions[0]
                ? response?.data?.workbench_permissions[0]
                : 0
            ));
          }
        });
    } else {
      dispatch(setWorkBenchName(""));
      dispatch(setModelTypeFromWorkbench(""));
    }
    dispatch(setDashboardModalsOpens(false));
  }, [secret_key, viewPortStatus]);

  useEffect(() => {
    if (
      viewPortStatus &&
      getNodesData?.data?.nodes &&
      getEdgesData?.data?.edges
    ) {
      const nextNodes: any = getNodesData?.data?.nodes.map((node: any) => {
        return {
          ...node,
          data: { ...node.data, ...getNodeMethods({ methodType: node.type, ndsPositionMap: getNodesData?.data?.nodes_position }) }
        };
      });
      setNodes(nextNodes);
      setEdges(getEdgesData?.data?.edges);
      updateNodesPosition(getNodesData?.data?.nodes_position);
    }
  }, [viewPortStatus, getNodesData?.data?.nodes, getEdgesData?.data?.edges, getNodesData?.data?.nodes_position]);

  //Generate Model API Call
  useEffect(() => {
    if (generateModel) {
      const value = hasInvalidData(nodes);
      const validateConnection = checkParentNodeConnections(nodes, edges);

      if (value) {
        showErrorToast("All questions and answers must be filled in.");
        dispatch(setGenerateModel(false));
        return;
      } else if (validateConnection?.isValid === false) {
        showErrorToast("The connection terminated somewhere. Please check the connection");
        dispatch(setGenerateModel(false));
        return;
      } else {
        let viewPortPayload = {
          format_type: "2",
          viewport_data: {
            nodes: nodes,
            edges: edges,
            nodesPosition: nodesPositionMap
          },
          input_data: transformNodesToQA(nodes, edges),
          embedded_name: "",
          workspace_name: validWorkBenchName,
          context: modelGenerateContextData,
        };

        let queryParam = workBenchName && formatType !== 1 ? secret_key : "";

        if (validWorkBenchName) {
          generateWorkbenchModel({
            values: viewPortPayload,
            queryParam: queryParam,
          })
            .unwrap()
            .then((response: any) => {
              if (response?.status_code === 201) {
                showSuccessToast(response?.message);
                setIsGenerateModelContextOpen(false);
                navigate(ROUTES.VIEWMODELS)
                // setIsPendingStatusModalOpen(true);
                sessionStorage.setItem("secret_key", response?.data?.secret_key);
                sessionStorage.setItem("id", response?.data?.record_id);
                // for the feature changes nav is commented
                // navigate("/dashboard");
                // dispatch(setWorkBenchName(""));
              } else if (response?.status_code === 400) {
                showErrorToast(response?.message);
              } else if (response?.status_code === 200) {
                Modal.warning({
                  title: "Warning",
                  content: response?.message,
                  className: "warningModal",
                  centered: true,
                  onOk: () => {
                    setIsGenerateModelContextOpen(false);
                    // setIsPendingStatusModalOpen(true);
                  },
                });
                navigate(ROUTES.VIEWMODELS)
              }
            })
            .catch((error: any) => {
              // showErrorToast(error?.response?.data?.message);
            });
        } else {
          showErrorToast("Please enter Workbench name to match the condition");
        }
        dispatch(setGenerateModel(false));
      }
    }
  }, [generateModel]);

  //Draft Model API Call
  useEffect(() => {
    if (draftModel) {
      let viewPortDraftPayload = {
        format_type: "2",
        viewport_data: {
          nodes: nodes,
          edges: edges,
          nodesPosition: nodesPositionMap,
        },
        input_data: transformNodesToQA(nodes, edges),
        embedded_name: "",
        workspace_name: validWorkBenchName,
      };

      let queryParam = workBenchName ? secret_key : "";

      if (validWorkBenchName) {
        draftWorkbenchModel({
          values: viewPortDraftPayload,
          queryParam: queryParam,
        })
          .unwrap()
          .then((response: any) => {
            if (response?.status_code === 201) {
              showSuccessToast(response?.message);
              navigate("/dashboard");
              dispatch(setWorkBenchName(""));
            } else if (response?.status_code === 400) {
              showErrorToast(response?.message);
            }
          })
          .catch((error: any) => {
            // showErrorToast(error?.response?.data?.message);
          });
      } else {
        showErrorToast("Please enter Workbench name");
      }
      dispatch(setDraftModel(false));
    }
  }, [draftModel]);

  useEffect(() => {
    if (reactFlowInstance && zoomContext.workbenchZoomLevel) {
      reactFlowInstance.zoomTo(zoomContext.workbenchZoomLevel);
    }
  }, [reactFlowInstance, zoomContext.workbenchZoomLevel]);

  useEffect(() => {
    if (nodesWithPosition?.length > 0) {
      // Find leftmost x position
      const leftMostX = Object.values(nodesPositionMap).reduce((min: any, node: any) => {
        return Math.min(min, node?.x);
      }, Infinity) as number;

      if (leftMostX > 50) {
        // Shift all nodes to start from x = 50
        const adjustment = leftMostX - 50;

        const updatedNodesMap = {} as any;
        Object.keys(nodesPositionMap).forEach((key: any) => {
          updatedNodesMap[key] = {
            x: nodesPositionMap[key]?.x - adjustment,
            y: nodesPositionMap[key]?.y,
          };
        });

        updateNodesPosition(nodesWithPosition)
      }
    }
  }, []);

  const centerNodeInViewport = useCallback((nodeId: string) => {
    const node: any = getNode(nodeId);
    const nodePosition: any = (node?.position || nodesPositionMap[nodeId]);

    if (node && nodePosition) {
      // Center the node
      setCenter(
        nodePosition?.x + node?.width / 2,
        nodePosition?.y + node?.height / 2,
        { duration: 800 } // Optional smooth animation
      );
    }
  }, [getNode, nodesPositionMap, setCenter]);

  // Sync input value when zooming with mouse scroll
  const handleViewportChange = useCallback((_: any, viewport: any) => {
    const ZOOM = viewport.zoom.toFixed(2);
    zoomContext?.getZoomValue(ZOOM * 100);
    viewPortContext?.getViewPortXYValue(viewport?.x, viewport?.y)
  }, [viewPortContext, zoomContext]);

  const onConnect = useCallback(
    (params: any) => {
      const targetNode: any = nodes?.find(
        (node: any) => node?.id === params?.target
      );
      const targetNodePosition: any = nodesPositionMap?.[params?.target];

      const sourceNode: any = nodes?.find(
        (node: any) => node?.id === params?.source
      );
      const sourceNodePosition: any = nodesPositionMap?.[params?.source];

      // Check if target node already has an incoming connection
      const hasIncomingEdge = edges?.some(
        (edge: any) => edge?.target === params?.target && edge?.source !== ""
      );

      if (!sourceNode || !targetNode) return;

      // Check if qaNode already has an outgoing connection
      const hasQANodeOutgoingEdge =
        sourceNode?.type === "qaNode" &&
        edges?.some((edge: any) => edge?.source === params?.source);

      const hasAddButtonNodeOutgoingEdge =
        sourceNode?.type === "addButtonNode" &&
        edges?.some((edge: any) => edge?.source === params?.source);

      // Prevent direct connection from questionNode to descriptionNode/stepsNode
      const isInvalidQuestionNodeConnection =
        sourceNode?.type === "questionNode" &&
        (targetNode?.type === "descriptionNode" || targetNode?.type === "stepsNode");

      // Prevent direct connection from choiceNode to restricted nodes
      const isInvalidChoiceNodeConnection =
        sourceNode?.type === "choiceNode" &&
        (targetNode?.type === "descriptionNode" ||
          targetNode?.type === "stepsNode" ||
          targetNode?.type === "uploadNode" ||
          targetNode?.type === "uploadButtonNode" || targetNode?.type === "questionNode");

      if (isInvalidQuestionNodeConnection) {
        showErrorToast("Cannot connect questionNode directly to descriptionNode or stepsNode");
        return;
      }

      if (isInvalidChoiceNodeConnection) {
        showErrorToast("Choice nodes cannot be directly connected to description, steps, upload, or upload button nodes");
        return;
      }

      // Validate qaNode connection
      if (hasQANodeOutgoingEdge) {
        showErrorToast("QA node can only have one outgoing connection");
        return;
      }

      // Validate addButtonNode connection
      if (hasAddButtonNodeOutgoingEdge) {
        showErrorToast("Add button node without label can only have one outgoing connection");
        return;
      }

      if (
        targetNodePosition?.x > sourceNodePosition?.x &&
        !(
          sourceNode?.type === "addButtonNode" &&
          targetNode?.type === "questionNode"
        )
      ) {
        // Only check for existing connections if attempting to make a new one
        if (hasIncomingEdge) {
          showErrorToast("Target node already has an incoming connection");
          return;
        }

        if (params?.source !== params?.target) {
          const newEdge = {
            ...params,
            animated: true,
            type: "smoothstep",
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          };

          setEdges((eds: any) => addEdge(newEdge, eds));
        }
      }
    },
    [nodes, nodesPositionMap, edges]
  );

  const handleInit = useCallback(
    (instance: any) => {
      const zoomLevel = zoomContext.workbenchZoomLevel;
      instance.setViewport({ x: 290, y: 10, zoom: zoomLevel || 0.5 });

      setReactFlowInstance(instance);
    },
    [zoomContext.zoomValue, color]
  );

  const onDragOver = useCallback(
    (event: any) => {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    },
    []
  );

  const onDrop = useCallback(
    (event: any) => {
      event.preventDefault();
      // check if the dropped element is valid
      if (!templateType) {
        return;
      }
      // Get the drop position from the event
      const position = screenToFlowPosition({
        x: event?.clientX ,
        y: event?.clientY,
      });
      
      position.x = Math.max(50, position?.x );

      const newNodeId = uuidv4(); // Get a unique ID for the new node

      // We need to pass the updated position map to the addNode function
      const updatedNodesPositionMap = {
        ...nodesPositionMap,
        [newNodeId]: position,
      };

      // Create a unique ID for the new node (could use UUID or similar)
      if (templateType === "question") {
        const newNode = {
          id: newNodeId,
          type: "questionNode",
          data: { label: "parentNode", ...getNodeMethods({ methodType: "questionNode", ndsPositionMap: updatedNodesPositionMap }) },
        };
        setNodes((nds: any) => [...nds, newNode]);
        updateNodesPosition({ [newNodeId]: position });

        setEdges((eds: any) => [
          ...eds,
          {
            id: `e-${newNodeId}`,
            source: "",
            target: newNodeId,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
      } else if (templateType === "answer") {
        const newNode = {
          id: newNodeId,
          type: "addButtonNode",
          // position,
          data: { label: "answerNode", ...getNodeMethods({ methodType: "addButtonNode", ndsPositionMap: updatedNodesPositionMap }) },
        };
        setNodes((nds: any) => [...nds, newNode]);
        updateNodesPosition({ [newNodeId]: position });

        setEdges((eds: any) => [
          ...eds,
          {
            id: `e-${newNodeId}`,
            source: "",
            target: newNodeId,
            type: "smoothstep",
            animated: true,
            style: { stroke: "#CCCCCC", strokeWidth: 2 },
          },
        ]);
      } else if (["paragraph", "steps", "yesorno", "link"].includes(templateType)) {
        const { flowData, positionsMap } = getNodesAndEdgesBasedONDrag(position, templateType);

        const overallPositionsMap = { ...updatedNodesPositionMap, ...positionsMap[templateType] }
        const nextNodes = flowData?.[templateType]?.nodes?.map((node: any) => {
          return {
            ...node,
            data: {
              ...node.data,
              ...getNodeMethods({ methodType: node.type, ndsPositionMap: overallPositionsMap }),
            },
          };
        });
        setNodes((nds: any) => [...nds, ...nextNodes]);
        setEdges((eds: any) => [...eds, ...flowData?.[templateType]?.edges]);
        updateNodesPosition(overallPositionsMap);
      } else {
        //Do Nothing
      }
    },
    [templateType, nodes, setNodes, setEdges, nodesPositionMap]
  );

  // Create the custom SVG with plus symbol
  const createCustomSVG = useCallback((opacity = 0.7) => {
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path transform="translate(47,47)" d="M2.59511 5.99527V0H3.40489V5.99527H2.59511ZM0 3.40016V2.59511H6V3.40016H0Z" fill="${color === "gray" || color === "green" ? "white" : "black"}" fill-opacity="${opacity}"/>
      </svg>
    `)}`;
  }, [color]);

  // Custom background pattern creator with viewport-aware positioning
  const getBackgroundPattern = useMemo(() => {
    const gridSize = 100;
    const scaledGridSize = gridSize * zoom;

    // Calculate offset to maintain grid alignment
    const xOffset = x % scaledGridSize;
    const yOffset = y % scaledGridSize;

    return {
      // backgroundColor: '#1a1a1a',
      backgroundImage: `url("${createCustomSVG()}")`,
      backgroundSize: `${scaledGridSize}px ${scaledGridSize}px`,
      backgroundPosition: `${xOffset}px ${yOffset}px`,
      backgroundRepeat: "repeat",
      transition: "none",
    };
  }, [zoom, x, y, createCustomSVG]);

  const handleClickOutside = useCallback(() => {
    dataNodeContext.getNodeData("", "");
  }, [dataNodeContext.getNodeData]);

  const handleNodeClick = useCallback(() => {
    dataNodeContext.getNodeData("", "");
  }, [dataNodeContext.getNodeData]);

  const handleOnNodesChange = useCallback((changes: any) => {
    if (changes.length === 1) {
      const isDragging = changes[0].dragging
      if (isDragging) onNodeDrag(changes);
    } else {
      onNodesChange(changes);
    }
  }, [])

  return (
    <div className={`flow-container ${flowContainerClass}`} ref={reactFlowWrapper}>
      <ReactFlow
        style={{ width: '100%', height: '100%' }}
        nodes={nodesWithPosition}
        edges={edges}
        onNodesChange={handleOnNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onPaneClick={handleClickOutside}
        onNodeClick={handleNodeClick}
        defaultViewport={{ x: 500, y: 10, zoom: zoomLevel || 1 }}
        fitViewOptions={{
          padding: 0.1,
          includeHiddenNodes: false,
        }}
        onInit={handleInit}
        onMove={handleViewportChange}
        panOnScroll
      >
        <Background
          color={color === "gray" || color === "green" ? 'rgba(255, 255, 255, 0.5)' : '#8a8a8a'}
          gap={10}
          size={1}
          style={getBackgroundPattern}
        />
        <GlobeMinimap
          width={350}
          height={220}
          className="custom-globe"
        />
      </ReactFlow>
    </div>
  );
});

const QuestionFlow = () => (
  <ReactFlowProvider>
    <Flow />
  </ReactFlowProvider>
);

export default memo(QuestionFlow);
